import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService, ResponseObject } from './auth.service';
import { ServiceBase } from './service.base';
import { environment } from 'src/environments/environment';
import { SaveCredentialRequest, SaveFunctionBindingRequest, TestCredentialRequest } from '../models/platform-credential.model';

/**
 * Platform credential administration (`/PlatformCredential`, SuperAdmin only).
 *
 * Every method here is either a masked read or a write. **None returns a credential value** — the
 * API has no route that does, deliberately. Do not add one: a secret that cannot be read back cannot
 * be leaked by this client.
 */
@Injectable({
  providedIn: 'root'
})
export class PlatformCredentialService extends ServiceBase {
  private api: string = '/PlatformCredential';

  constructor(private http: HttpClient, authService: AuthService) {
    super(authService);
  }

  /** All known providers with their masked credential status. */
  public fetchCredentials(): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/fetchCredentials';
    return this.http.get<ResponseObject>(url, httpOptions);
  }

  /** Create or rotate one credential. Returns the masked status, never the value. */
  public saveCredential(request: SaveCredentialRequest): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/saveCredential';
    return this.http.post<ResponseObject>(url, request, httpOptions);
  }

  public removeCredential(credentialKey: string): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/removeCredential/' + encodeURIComponent(credentialKey);
    return this.http.delete<ResponseObject>(url, httpOptions);
  }

  /**
   * Probe a provider. With `Value` set it tests the candidate key the operator has just typed;
   * without, it tests whatever credential is currently in force.
   */
  public testConnection(request: TestCredentialRequest): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/testConnection';
    return this.http.post<ResponseObject>(url, request, httpOptions);
  }

/** Every AI-requiring feature, what it resolves to, and the providers that may serve it. */
  public fetchFunctions(): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/fetchFunctions';
    return this.http.get<ResponseObject>(url, httpOptions);
  }

  /** Assign a provider to a feature, or switch the feature off. */
  public saveFunctionBinding(request: SaveFunctionBindingRequest): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/saveFunctionBinding';
    return this.http.post<ResponseObject>(url, request, httpOptions);
  }

  /** The stored health picture, without probing. */
  public fetchHealth(): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/fetchHealth';
    return this.http.get<ResponseObject>(url, httpOptions);
  }

  /** Probe every provider now. Sends no alerts - that is the scheduled check.  */
  public runHealthCheck(): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/runHealthCheck';
    return this.http.post<ResponseObject>(url, {}, httpOptions);
  }

  public fetchSpend(): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const url: string = environment.BASE_API_PATH + this.api + '/fetchSpend';
    return this.http.get<ResponseObject>(url, httpOptions);
  }

  public fetchAuditTrail(credentialKey?: string, take: number = 50): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    let url: string = environment.BASE_API_PATH + this.api + '/auditTrail?take=' + take;
    if (credentialKey) {
      url += '&credentialKey=' + encodeURIComponent(credentialKey);
    }
    return this.http.get<ResponseObject>(url, httpOptions);
  }
}
